import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Warehouse, ArrowLeftRight } from "lucide-react";
import { usePinPrompt } from "@/components/app/PinPrompt";
import TransferDialog from "@/components/app/TransferDialog";

const emptyWh = { name: "", address: "", bins: "" };

export default function Warehouses() {
  const pinPrompt = usePinPrompt();
  const [whs, setWhs] = useState([]);
  const [stock, setStock] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [xfer, setXfer] = useState(false);
  const [f, setF] = useState(emptyWh);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const [w, s] = await Promise.all([api.get("/warehouses"), api.get(`/warehouses/stock${filter ? `?warehouse_id=${filter}` : ""}`)]);
    setWhs(w.data); setStock(s.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const save = async () => {
    if (!f.name.trim()) return toast.error("Warehouse name required");
    await pinPrompt();
    const body = { name: f.name, address: f.address, bins: f.bins.split(",").map((b) => b.trim()).filter(Boolean) };
    try {
      if (editingId) await api.patch(`/warehouses/${editingId}`, body); else await api.post("/warehouses", body);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete warehouse?")) return;
    await pinPrompt();
    try { await api.delete(`/warehouses/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="overline">Inventory</div>
          <h1 className="text-3xl font-bold tracking-tight">Warehouses & Bins</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setXfer(true)} disabled={!whs.length} data-testid="wh-transfer-btn"><ArrowLeftRight size={14} /> Transfer Stock</Button>
          <Button className="btn-primary" onClick={() => { setF(emptyWh); setEditingId(null); setOpen(true); }} data-testid="wh-add-btn"><Plus size={14} /> Add Warehouse</Button>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        {whs.length === 0 && <div className="text-sm text-muted-foreground bg-white border border-border rounded-lg p-6 md:col-span-3">No warehouses yet. Add one (e.g. "Main Godown") with bins like A1, A2, Rack-3.</div>}
        {whs.map((w) => (
          <div key={w._id_str} className="bg-white border border-border rounded-lg p-4" data-testid={`wh-card-${w._id_str}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2 font-semibold"><Warehouse size={16} className="text-[#064E3B]" /> {w.name}</div>
              <div className="flex gap-1">
                <button className="p-1 hover:bg-slate-100 rounded" onClick={() => { setF({ name: w.name, address: w.address || "", bins: (w.bins || []).join(", ") }); setEditingId(w._id_str); setOpen(true); }} data-testid={`wh-edit-${w._id_str}`}><Pencil size={13} /></button>
                <button className="p-1 hover:bg-red-50 text-red-600 rounded" onClick={() => del(w._id_str)} data-testid={`wh-del-${w._id_str}`}><Trash2 size={13} /></button>
              </div>
            </div>
            <div className="text-xs text-slate-500 mt-1">{w.address || "—"}</div>
            <div className="flex flex-wrap gap-1 mt-2">{(w.bins || []).map((b) => <span key={b} className="chip chip-slate">{b}</span>)}</div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mb-2">
        <div className="overline">Stock by location</div>
        <select className="border border-border rounded-md h-9 px-2 bg-white" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="wh-filter">
          <option value="">All warehouses</option>
          {whs.map((w) => <option key={w._id_str} value={w._id_str}>{w.name}</option>)}
        </select>
      </div>
      <div className="bg-white border border-border rounded-lg overflow-x-auto">
        <table className="acc-table w-full">
          <thead><tr><th>Item</th><th>Warehouse</th><th>Bin</th><th className="num">Qty</th></tr></thead>
          <tbody>
            {stock.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-muted-foreground">No stock recorded.</td></tr>}
            {stock.map((s) => (
              <tr key={`${s.item_id}-${s.warehouse_id}-${s.bin}`}>
                <td className="font-semibold">{s.item_name}</td>
                <td>{s.warehouse_name === "Unassigned" ? <span className="chip chip-amber">Unassigned</span> : s.warehouse_name}</td>
                <td className="font-mono">{s.bin || "—"}</td>
                <td className="num">{s.qty} {s.unit}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingId ? "Edit" : "Add"} Warehouse</DialogTitle></DialogHeader>
          <div><Label>Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="wh-name-input" /></div>
          <div><Label>Address</Label><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></div>
          <div><Label>Bins (comma separated)</Label><Input value={f.bins} onChange={(e) => setF({ ...f, bins: e.target.value })} placeholder="A1, A2, B1, Cold-Room" data-testid="wh-bins-input" /></div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="btn-primary" onClick={save} data-testid="wh-save-btn">Save</Button></div>
        </DialogContent>
      </Dialog>
      <TransferDialog open={xfer} onClose={() => setXfer(false)} warehouses={whs} onDone={load} />
    </div>
  );
}
